import axios from "axios";
import { proxyUrl } from "../config";
import { setupInterceptorsTo } from "./Interceptors";
setupInterceptorsTo(axios);

interface PortfolioModelData
{
    portfolioModelId: number,
    portfolioModelName: string
}
export interface PortfolioModelAssociated
{
    portfolioModelCount: number,
    portfolioModelData: PortfolioModelData[]
}
interface IModelWallet
{
    id: number,
    name: string,
	savingPlan: any,
    savingPlanId: number,
    savingPlanType: number,
    userSubscriptionId?: number,
    contract?: string,
    jsonFundsData: DataAndCartLines[]
}
export type ModelWallet = IModelWallet;

interface IModelWalletSelector
{
    key: string,
    text: string,
    id: string
}
export type ModelSavingPlanSelector = IModelSavingPlanSelector;

interface IModelSavingPlanSelector
{
    value: number,
    label: string
}
export type ModelWalletSelector = IModelWalletSelector;

interface IModelWalletOptions
{
    value: number,
    label: string
}
export type ModelWalletOptions = IModelWalletOptions;

interface IFullModelPortfolio
{
    id: number,
    name: string,
    savingPlanId: number,
    fundItems: IFundItems
}
interface IFundItems
{
    name: string,
    isin: string,
    exchangeType: number,
    currency: string,
    allFundsId: number,
    ironIAScore: number
}

export type FullModelPortfolio = IFullModelPortfolio;

type saveWalletBodyData =
{
    name: string,
    jsonFundsData?: DataAndCartLines[]
}
export type DataAndCartLines =
{
    fund: CartLinesFromDB,
    savingPlanId: number,
    readOnly: boolean,
    modelPercentage: number
}
export interface IPortfolioModelHistoric
{
    compositionId: number,
    date: string,
    portfolioModelId: number,
    fundsData: IFundData[]
}
export interface IFundData
{
    fund: IFundItem,
    savingPlanId: number,
    modelPercentage: number,
    readOnly: boolean
}
export interface IFundItem
{
    name: string,
    isin: string,
    finametrixId: string,
    allFundsId: number
}
interface ICompositionItem
{
    date: string,
    isinAndWeight: {[key: string]: number}
}
export interface CompositionFromBody
{
    elementsUsed?: {[key: number]: number} | null,
    composition: ICompositionItem[],
    isImportation: boolean,
    portfolioModelId: number,
    userRequestPortfolioModelChange?: boolean
}
export interface CompositionResult
{
    status: boolean,
    message: string
}
export interface CompositionToDelete
{
    portfolioModelId: number,
    compositionId: number,
    composition: ICompositionItem[]
}
export type CartLinesFromDB =
{
    name: string,
    available?: boolean,
    isin: string,
    titles?: number,
    bought?: string,
    valuation?: string,
    revaluation?: string,
    revaluationPercentage?: string,
    weight: string,
    vLiquid?: string,
    exchangeRate?: number,
    liquidDate?: string,
    benchmarkFinametrixId?: string,
    managementFee?: number,
    noMin?: boolean,
    allFundsId: number,
    minimumInvestment?: number,
    ongoingCharges?: number,
    rebate?: number,
    complexity?: boolean,
    finametrixId?: string,
    instrumentId?: string,
    instrumentType?: number
}
export interface IRatioValue
{
    [period: string]: number;
}

export interface IRatioData
{
    ratioName: string;
    ratioDisplayName: string;
    values: IRatioValue;
}

export interface IDataRatios
{
    ratios: IRatioData[];
    periods: string[];
}

export interface IFullPortfolioModelResponse
{
    status: number;
    data:
    {
        dataPortfolio:
        {
            id: number;
            name: string;
            savingPlanId: number;
            fundItems: any[];
        };
        dataReportingPortfolio:
        {
            series: any[];
        };
        dataRatios: IDataRatios;
    };
    message: string;
}

class PortfolioModelWalletService
{
    public getModelWallet = async (portfolioModelId: number): Promise<ModelWallet> =>
    {
        let url: string = `${proxyUrl}/portfolioModels/${portfolioModelId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public saveModelWallet = async (savingPlanId: number, walletName: string, cartLines: DataAndCartLines[]): Promise<ModelWallet> =>
    {
        let url: string = `${proxyUrl}/portfolioModelsBySavingsPlan/${savingPlanId}`;
        let body: saveWalletBodyData = {name: walletName, jsonFundsData: cartLines};
        
        const req = await axios.post<ModelWallet>(url, JSON.stringify(body));
        return req.data;
    }
    public updateModelWallet = async (walletName: string, portfolioModelId: number, cartLines: DataAndCartLines[]): Promise<ModelWallet> =>
    {
        let url: string = `${proxyUrl}/portfolioModels/${portfolioModelId}`;
        let body: saveWalletBodyData = {name: walletName, jsonFundsData: cartLines};
        const req = await axios.patch<ModelWallet>(url, JSON.stringify(body));
        return req.data;
    }
    public renameModelWallet = async (name: string, portfolioModelId: number): Promise<ModelWallet> =>
    {
        let url: string = `${proxyUrl}/portfolioModels/${portfolioModelId}`;
        let body: saveWalletBodyData = {name: name};
        const req = await axios.patch(url, body);
        return req.data;
    }
    public deleteModelWallet = async (portfolioModelId: number): Promise<ModelWallet> =>
    {
        let url: string = `${proxyUrl}/portfolioModels/${portfolioModelId}`;
        const req = await axios.delete(url);
        return req.data;
    }
    public savingPlanHasPortfolioModel = async (savingPlanId: number): Promise<PortfolioModelAssociated> =>
    {
        let url: string = `${proxyUrl}/SavingPlanHasPortfolioModel/${savingPlanId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public getModelWallets = async (savingPlanId: number): Promise<ModelWallet[]> =>
    {
        let url: string = `${proxyUrl}/portfolioModelsBySavingsPlan/${savingPlanId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public createVirtualOrders = async (portfolioModelId: number): Promise<ModelWallet[]> =>
    {
        let url: string = `${proxyUrl}/portfolioModels/${portfolioModelId}/virtualOrders`;
        const req = await axios.post(url, null);
        return req.data;
    }
    public getFullPortfolioModelInformation = async (portfolioModelId: number): Promise<any> =>
    {
        let url: string = `${proxyUrl}/FullPortfolioModelInformation/${portfolioModelId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public getAllPortfolioModelHistoricByPortfolioModelId = async (portfolioModelId: number): Promise<{data: IPortfolioModelHistoric[]}> =>
    {
        let url: string = `${proxyUrl}/GetAllPortfolioModelHistoricByPortfolioModelId/${portfolioModelId}`;
        const req = await axios.get(url);
        return req;
    }
    public getAllPortfolioModelHistoricBySavingPlanId = async (savingPlanId: number): Promise<any> =>
    {
        let url: string = `${proxyUrl}/GetAllPortfolioModelHistoricBySavingPlanId/${savingPlanId}`;
        const req = await axios.get(url);
        return req;
    }
    public getAllPortfolioModelHistoricByDateRange = async (userId: number, dateFrom: string, dateTo: string): Promise<any> =>
    {
        let url: string = `${proxyUrl}/GetAllPortfolioModelHistoricByDateRange/${userId}/${dateFrom}/${dateTo}`;
        const req = await axios.get(url);
        return req;
    }
    public createCompositions = async (body: CompositionFromBody): Promise<CompositionResult> =>
    {
        let url: string = `${proxyUrl}/CreateCompositions`;
        const req = await axios.post(url, body);
        return req.data;
    }
    public deleteComposition = async (body: CompositionToDelete): Promise<CompositionResult> =>
    {
        let url: string = `${proxyUrl}/DeleteComposition`;
        const req = await axios.post(url, body);
        return req.data;
    }
}

const portfolioModelWallets = new PortfolioModelWalletService();
export default portfolioModelWallets;